import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
  CaseReducer,
} from '@reduxjs/toolkit';
import {
  DataUsers,
  ParamsGetUsers,
  User,
  UserModal,
  ModifyUser,
  ChangePasswordData,
} from '../models/user';

import userService from '../services/user';
import { RequestLoading } from '@app/utils/constants';
import { handleErrorResponse } from '@app/utils/helpers';

const requests = ['getUsers', 'getUserDetail', 'changePassword'] as const;

type UserRequest = RequestLoading<(typeof requests)[number]>;

interface State {
  dataUsers: DataUsers;
  getUserLoading: boolean;
  selectedUser: User | undefined;
  searchValue: string | undefined;
  userModal: UserModal;
  userRequest: UserRequest;
}

const initialState: State = {
  dataUsers: {
    data: [],
  },
  selectedUser: undefined,
  searchValue: undefined,
  getUserLoading: false,
  userModal: {
    visible: false,
  },
  userRequest: {
    loading: false,
  },
};

type CR<T> = CaseReducer<State, PayloadAction<T>>;

const TYPE_PREFIX = 'admin/management-customer/customer/';

const getUsers = createAsyncThunk<DataUsers, ParamsGetUsers>(
  TYPE_PREFIX + 'getUsers',
  async (params, { rejectWithValue }) => {
    try {
      const result = await userService.getUsers(params);
      return result;
    } catch (error) {
      return rejectWithValue(handleErrorResponse(error));
    }
  },
);

const setSearchValueCR: CR<{ value: string | undefined }> = (
  state,
  action,
) => ({
  ...state,
  searchValue: action.payload.value,
});

const setUserModalVisibleCR: CR<UserModal> = (state, action) => ({
  ...state,
  userModal: action.payload,
});

// const addNewUser = createAsyncThunk(
//   'admin/management-customer/customer/addNewUser',
//   async (data: Us) => {
//     const result = await userService.createUser();
//   },
// );

const updateUser = createAsyncThunk(
  'admin/management-customer/customer/updateUser',
  async (_: { id: string; data: ModifyUser }) => {
    const result = await userService.updateUser(_.id, _.data);
    return result;
  },
);

const getUserDetail = createAsyncThunk<User, string>(
  'admin/management-customer/customer/getUserDetail',
  async (userId, { rejectWithValue }) => {
    try {
      const result = await userService.getUserDetail(userId);
      return result;
    } catch (error) {
      return rejectWithValue(handleErrorResponse(error));
    }
  },
);

const changeUserPassword = createAsyncThunk<string, ChangePasswordData>(
  TYPE_PREFIX + 'changeUserPassword',
  async (data, { rejectWithValue }) => {
    try {
      const result = await userService.changePassword(data);

      return result;
    } catch (error) {
      return rejectWithValue(handleErrorResponse(error));
    }
  },
);

const updateUserStatus = createAsyncThunk<
  User,
  { id: string; isActive: boolean }
>(
  TYPE_PREFIX + 'updateUserStatus',
  async ({ id, isActive }, { rejectWithValue }) => {
    try {
      const result = await userService.updateUserStatus(id, isActive);
      return result;
    } catch (error) {
      return rejectWithValue(handleErrorResponse(error));
    }
  },
);

// const searchTextUser = createAsyncThunk(
//   'admin/management-customer/customer/searchTextUser',
//   async (arg: { username: string, email: string, phoneNumber: string, fullName: string }) => {
//     const result = await userService.searchTextUser(
//       arg.email, arg.fullName, arg.phoneNumber, arg.username
//     );
//     return result;
//   },
// );
const slice = createSlice({
  name: 'admin/management-customer/customer',
  initialState,
  reducers: {
    setSelectedUser: (state, action) => {
      // state.selectedUser = action.payload;
    },
    resetDataTable: (state) => {
      // state.dataTable = {
      //   data: [],
      //   paging: undefined,
      // };
    },
    setSearchValue: setSearchValueCR,
    setUserModalVisible: setUserModalVisibleCR,
  },
  extraReducers: (builder) => {
    // getUser
    builder.addCase(getUsers.pending, (state, action) => ({
      ...state,
      // getUserLoading: true,
      userRequest: {
        loading: true,
        name: 'getUsers',
        status: action.meta.requestStatus,
      },
    }));
    builder.addCase(getUsers.fulfilled, (state, action) => ({
      ...state,
      dataUsers: action.payload,
      // getUserLoading: false,
      userRequest: {
        loading: false,
        name: 'getUsers',
        status: action.meta.requestStatus,
      },
    }));
    builder.addCase(getUsers.rejected, (state, action) => ({
      ...state,
      // getUserLoading: false,
      userRequest: {
        loading: false,
        name: 'getUsers',
        status: action.meta.requestStatus,
        error: action.payload ?? action.error,
      },
    }));

    // updateUser
    builder.addCase(updateUser.pending, (state) => ({
      ...state,
      getUserLoading: true,
    }));
    builder.addCase(updateUser.fulfilled, (state, { payload }) => ({
      ...state,
      selectedUser: payload,
      getUserLoading: false,
    }));
    builder.addCase(updateUser.rejected, (state) => ({
      ...state,
      getUserLoading: false,
    }));

    // getUserDetail
    builder.addCase(getUserDetail.pending, (state, action) => ({
      ...state,
      // getUserLoading: true,
      userRequest: {
        loading: true,
        name: 'getUserDetail',
        status: action.meta.requestStatus,
      },
    }));
    builder.addCase(getUserDetail.fulfilled, (state, action) => ({
      ...state,
      selectedUser: action.payload,
      // getUserLoading: false,
      userRequest: {
        loading: false,
        name: 'getUserDetail',
        status: action.meta.requestStatus,
      },
    }));
    builder.addCase(getUserDetail.rejected, (state, action) => ({
      ...state,
      // getUserLoading: false,
      userRequest: {
        loading: false,
        name: 'getUserDetail',
        status: action.meta.requestStatus,
        error: action.payload ?? action.error,
      },
    }));

    // change current user password
    builder.addCase(changeUserPassword.pending, (state, action) => ({
      ...state,
      userRequest: {
        loading: true,
        name: 'changePassword',
        status: action.meta.requestStatus,
      },
    }));
    builder.addCase(changeUserPassword.fulfilled, (state, action) => ({
      ...state,
      userRequest: {
        loading: false,
        name: 'changePassword',
        status: action.meta.requestStatus,
      },
    }));
    builder.addCase(changeUserPassword.rejected, (state, action) => ({
      ...state,
      userRequest: {
        loading: false,
        name: 'changePassword',
        status: action.meta.requestStatus,
        error: action.payload ?? action.error,
      },
    }));

    // searchTextUser
    // builder.addCase(searchTextUser.pending, (state) => ({
    //   ...state,
    //   getCustomersLoading: true,
    // }));
    // builder.addCase(searchTextUser.fulfilled, (state, { payload }) => ({
    //   ...state,
    //   dataTable: payload,
    //   getCustomersLoading: false,
    // }));
    // builder.addCase(updateUser.rejected, (state) => ({
    //   ...state,
    //   getCustomersLoading: true,
    // }));
  },
});

export const {
  setSelectedUser,
  resetDataTable,
  setSearchValue,
  setUserModalVisible,
} = slice.actions;

export {
  getUsers,
  updateUser,
  getUserDetail,
  changeUserPassword,
  updateUserStatus,
};

export default slice.reducer;
